import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { ReactElement } from 'react';
import { useI18n, useToast } from '@xdocs/ui';
import { deployBroadcastPush, wsChannel } from '@xdocs/schemas';
import { useWs } from '../ws/WsProvider.js';
import { resolveBuildVersion } from '../lib/env.js';
import { applyUpdate, getUpdateState, notifyUpdateAvailable, onUpdateStateChange } from './updateStore.js';

/**
 * Renders nothing itself — a controller component that:
 * 1. Subscribes to `deploy:broadcast` (E17 §5.5) and compares the pushed
 *    `version` against `window`'s own build (`resolveBuildVersion()`,
 *    `VITE_BUILD_VERSION` stamped at build time). A mismatch means this tab
 *    is running a stale bundle relative to what the server just rolled out.
 * 2. Also listens to `./updateStore.js`, which `registerServiceWorker.ts`
 *    feeds from the SW's own `onNeedRefresh` — E9 §10.3's "belt-and-
 *    suspenders" (whichever of the two fires first shows the SAME toast;
 *    `updateStore`'s "first notifier wins" rule keeps it to exactly one).
 * 3. Shows exactly ONE non-blocking toast (E9: "Exactly one update-toast
 *    component") reading "A new version of xDocs is ready." with a
 *    `Reload` action that calls `applyUpdate()` (activates the waiting SW,
 *    or a plain reload if none is registered — e.g. the update was learned
 *    only from the server push, no new SW asset actually changed).
 *
 * Mount once near the app shell root (inside both `<WsProvider>` and
 * `<ToastProvider>`) — see `./WIRING.md` for the exact call site (`App.tsx`
 * is outside this task's ownership).
 */
export function DeployUpdateToast(): ReactElement | null {
  const { wsClient } = useWs();
  const { push } = useToast();
  const { t } = useI18n();
  const updateState = useSyncExternalStore(onUpdateStateChange, getUpdateState);
  const toastShownRef = useRef(false);

  useEffect(() => {
    return wsClient.subscribe(wsChannel.deployBroadcast, (envelope) => {
      const parsed = deployBroadcastPush.safeParse(envelope.payload);
      if (!parsed.success) return;
      if (parsed.data.version === resolveBuildVersion()) return;
      notifyUpdateAvailable('server', parsed.data.version);
    });
  }, [wsClient]);

  useEffect(() => {
    if (!updateState.available || toastShownRef.current) return;
    toastShownRef.current = true;
    push({
      tone: 'info',
      message: t('pwa:updateToast.message'),
      // No native "Undo" semantics here — this toast's ONE action IS the
      // primary action (reload to update), reusing `ToastInput`'s
      // undo-button slot as a generic action affordance since `@xdocs/ui`
      // doesn't yet expose a non-undo action button (see `./WIRING.md`).
      undoLabel: t('pwa:updateToast.reload'),
      onUndo: () => {
        void applyUpdate();
      },
      // Long-but-finite: the primitive has no "persist until dismissed"
      // mode (an unbounded `setTimeout` delay overflows and fires almost
      // immediately — a real JS footgun), so this is the longest safe
      // window rather than "forever". See `./WIRING.md`.
      durationMs: 10 * 60 * 1000,
    });
  }, [updateState.available, push, t]);

  return null;
}
